import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Brain, Heart, Users, RefreshCw, MessageCircle, ChevronRight } from 'lucide-react';

// Glass card variant styles - matching Regulation Hub design
const variantStyles = {
    sage: { glassClass: 'glass-card-sage', iconGlow: 'icon-glow-sage' },
    lavender: { glassClass: 'glass-card-lavender', iconGlow: 'icon-glow-lavender' },
    rose: { glassClass: 'glass-card-rose', iconGlow: 'icon-glow-rose' },
    blue: { glassClass: 'glass-card-blue', iconGlow: 'icon-glow-blue' }
};

const ADHDRelationships = () => {
    const navigate = useNavigate();
    const [fadeIn, setFadeIn] = useState(false);

    useEffect(() => {
        setTimeout(() => setFadeIn(true), 100);
    }, []);

    const topics = [
        {
            id: 'cycles',
            icon: RefreshCw,
            title: 'Understanding Cycles',
            description: 'How ADHD creates relationship cycles',
            path: '/relationship-hub/cycles',
            variant: 'lavender'
        },
        {
            id: 'communication',
            icon: MessageCircle,
            title: 'Communication Challenges',
            description: 'Why ADHD makes communication harder',
            path: '/communicate',
            variant: 'sage'
        },
        {
            id: 'attachment',
            icon: Heart,
            title: 'Attachment & ADHD',
            description: 'How ADHD affects attachment styles',
            path: '/relationship-hub/attachment-needs',
            variant: 'rose'
        },
        {
            id: 'coregulation',
            icon: Users,
            title: 'Co-Regulation',
            description: 'Learn to regulate together with your partner',
            path: '/regulate/coregulation',
            variant: 'blue'
        }
    ];

    return (
        <div className="min-h-screen bg-transparent">
            {/* Header */}
            <header className="sticky top-0 z-40 glass-header">
                <div className="max-w-xl mx-auto px-6 py-4 flex items-center gap-4">
                    <button 
                        onClick={() => navigate('/relationship-hub')}
                        className="p-2 -ml-2 rounded-full hover:bg-[#FFBB98]/40 transition-colors text-[#7D8E95] hover:text-[#1D6B6B]"
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <h1 className="text-lg font-semibold text-[#1D6B6B]">
                        ADHD & Relationships
                    </h1>
                </div>
            </header>

            <main className="max-w-xl mx-auto px-6 py-8">
                {/* Intro */}
                <div className={`text-center mb-8 transition-opacity duration-500 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}>
                    <div className="w-16 h-16 rounded-xl bg-[#1D6B6B]/10 flex items-center justify-center mx-auto mb-4">
                        <Brain className="w-8 h-8 text-[#1D6B6B]" />
                    </div>
                    <p className="text-[#5A7A7A] leading-relaxed">
                        Understanding how ADHD affects your relationships is the first step toward healthier connections.
                    </p>
                </div>

                {/* Topics - Glass card style matching Regulation Hub */}
                <div className="space-y-4">
                    {topics.map((topic, index) => {
                        const TopicIcon = topic.icon;
                        const style = variantStyles[topic.variant] || variantStyles.sage;
                        return (
                            <button
                                key={topic.id}
                                onClick={() => navigate(topic.path)}
                                className={`w-full ${style.glassClass} rounded-2xl p-5 cursor-pointer card-hover hover-shine brand-slide-up
                                         flex items-center gap-5
                                         ${fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}
                                style={{ 
                                    animationDelay: `${index * 60}ms`,
                                    transitionDuration: '500ms'
                                }}
                                data-testid={`topic-${topic.id}`}
                            >
                                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br from-[#2A8585] via-[#1D6B6B] to-[#145555] flex items-center justify-center flex-shrink-0 ${style.iconGlow}`}>
                                    <TopicIcon className="w-6 h-6 text-white" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-[#1D6B6B] mb-0.5">{topic.title}</p>
                                    <p className="text-sm text-[#2A8585]">{topic.description}</p>
                                </div>
                                <ChevronRight className="w-5 h-5 text-[#7D8E95] flex-shrink-0" />
                            </button>
                        );
                    })}
                </div>
            </main>
        </div>
    );
};

export default ADHDRelationships;
